<div class="container mx-auto space-y-6 px-4 py-6">
  <!-- ═══════════ Page header ═══════════ -->
  <header>
    <h1 hlmH3>{{ pageTitle }}</h1>
    <p hlmMuted class="mt-1">{{ pageSubtitle }}</p>
  </header>

  <!-- ═══════════ MEMBER ═══════════ -->
  @if (company?.is_member && company?.member_of_company_name) {
    <hlm-tabs [tab]="dashboardTab" class="w-full">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <hlm-tabs-list variant="line" class="w-fit">
          <button hlmTabsTrigger="member" (click)="onMemberTabChange('member')">
            Meine Einlösungen
          </button>
          @if (company?.member_can_see_reporting !== false) {
            <button hlmTabsTrigger="group" (click)="onMemberTabChange('group')">
              Netzwerk-Statistik
            </button>
          }
        </hlm-tabs-list>

        <app-date-range-filter
          [selected]="sharedDateRange"
          (changed)="onSharedDateRangeChange($event)" />
      </div>

      <div hlmTabsContent="member" class="space-y-4 pt-4">
        <section class="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <app-kpi-card
            accent="default"
            label="Eingelöster Betrag"
            [value]="money(memberStats?.total_redeemed_cents ?? 0)">
            <ng-template #icon><ng-icon name="lucideBanknote" /></ng-template>
          </app-kpi-card>
          <app-kpi-card
            accent="default"
            label="Anzahl Einlösungen"
            [value]="(memberStats?.redemption_count ?? 0).toString()">
            <ng-template #icon><ng-icon name="lucideTicketCheck" /></ng-template>
          </app-kpi-card>
          <!-- A company name, not a figure — reads wrong flush right -->
          <app-kpi-card
            accent="default"
            align="start"
            label="Mein Netzwerk"
            [value]="company?.member_of_company_name || ''"
            subtext="Mitglied">
            <ng-template #icon><ng-icon name="lucideUsers" /></ng-template>
          </app-kpi-card>
        </section>

        <ng-container *ngTemplateOutlet="searchBar"></ng-container>
        <app-redemption-table
          [records]="memberRedemptionRows"
          [loading]="memberRedemptionsLoading"
          [showPayout]="true"
          [searchQuery]="activeSearch"
          emptyMessage="Keine Einlösungen im gewählten Zeitraum." />
      </div>

      <div hlmTabsContent="group" class="space-y-4 pt-4">
        <div #statisticsContainer>
          <app-statistics [statistics]="(statistics$ | async) || undefined" />
        </div>
        <ng-container *ngTemplateOutlet="searchBar"></ng-container>
        <app-voucher-history
          [vouchers]="vouchers"
          [loading]="vouchersLoading"
          [currentPage]="currentPage"
          [totalPages]="totalPages"
          [totalItems]="totalItems"
          [currentSortField]="currentSortField"
          [currentSortOrder]="currentSortOrder"
          (onPageChange)="onPageChange($event)"
          (onSort)="onSort($event)" />
      </div>
    </hlm-tabs>
  }

  <!-- ═══════════ CENTRAL ACCOUNT (group admin) ═══════════ -->
  @else if (company?.is_group_admin) {
    <hlm-tabs [tab]="centralTab" class="w-full">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <hlm-tabs-list variant="line" class="w-fit">
          <button hlmTabsTrigger="stats" (click)="onCentralTabChange('stats')">
            Statistik & Gutscheine
          </button>
          <button hlmTabsTrigger="distribution" (click)="onCentralTabChange('distribution')">
            Einlösungen nach Mitglied
          </button>
        </hlm-tabs-list>

        <app-date-range-filter
          [selected]="sharedDateRange"
          (changed)="onSharedDateRangeChange($event)" />
      </div>

      <div hlmTabsContent="stats" class="space-y-4 pt-4">
        <div #statisticsContainer>
          <app-statistics [statistics]="(statistics$ | async) || undefined" />
        </div>
        <ng-container *ngTemplateOutlet="searchBar"></ng-container>
        <app-voucher-history
          [vouchers]="vouchers"
          [loading]="vouchersLoading"
          [currentPage]="currentPage"
          [totalPages]="totalPages"
          [totalItems]="totalItems"
          [currentSortField]="currentSortField"
          [currentSortOrder]="currentSortOrder"
          (onPageChange)="onPageChange($event)"
          (onSort)="onSort($event)" />
      </div>

      <div hlmTabsContent="distribution" class="space-y-4 pt-4">
        @if (transfersLoading) {
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
            @for (i of [1, 2, 3]; track i) {
              <hlm-skeleton class="h-28 w-full rounded-xl" />
            }
          </div>
        } @else if (transferOverview) {
          <section class="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <app-kpi-card
              accent="default"
              label="Gesamt eingelöst (Mitglieder)"
              [value]="money(transferOverview.total_redeemed_cents)">
              <ng-template #icon><ng-icon name="lucideBanknote" /></ng-template>
            </app-kpi-card>
            <app-kpi-card
              accent="default"
              label="Auszahlung an Mitglieder"
              [value]="money(transferOverview.total_payout_cents)">
              <ng-template #icon><ng-icon name="lucideArrowLeftRight" /></ng-template>
            </app-kpi-card>
            <app-kpi-card
              accent="default"
              label="Mitglieder"
              [value]="transferOverview.member_summaries.length.toString()">
              <ng-template #icon><ng-icon name="lucideUsers" /></ng-template>
            </app-kpi-card>
          </section>

          @if (transferOverview.member_summaries.length > 0) {
            <div hlmCard>
              <div hlmCardHeader>
                <h3 hlmCardTitle>Verteilung nach Mitglied</h3>
                <p hlmCardDescription>Anteil am eingelösten Gesamtbetrag</p>
              </div>
              <div hlmCardContent class="space-y-3">
                @for (
                  summary of transferOverview.member_summaries;
                  track summary.member_company_id
                ) {
                  <div class="flex items-center gap-4">
                    <div class="min-w-0 flex-1">
                      <p class="truncate text-sm font-medium">
                        {{ summary.member_company_name || summary.member_company_id }}
                      </p>
                      <p hlmMuted class="text-xs">
                        {{ summary.redemption_count }} Einlösung{{
                          summary.redemption_count !== 1 ? 'en' : ''
                        }}
                      </p>
                    </div>
                    <div hlmProgress class="w-24 shrink-0 sm:w-40" [value]="memberShare(summary)">
                      <div hlmProgressIndicator></div>
                    </div>
                    <p class="w-24 shrink-0 text-right text-sm font-medium tabular-nums">
                      {{ money(summary.total_redeemed_cents) }}
                    </p>
                  </div>
                }
              </div>
            </div>
          }

          <ng-container *ngTemplateOutlet="searchBar"></ng-container>
          <app-redemption-table
            [records]="centralTransferRows"
            [showMemberColumn]="true"
            [showPayout]="true"
            [searchQuery]="activeSearch"
            emptyMessage="Noch keine Einlösungen durch Mitglieder." />
        } @else {
          <hlm-empty class="py-12">
            <div hlmEmptyHeader>
              <div hlmEmptyMedia variant="icon"><ng-icon name="lucideUsers" /></div>
              <p hlmEmptyTitle>Keine Daten verfügbar</p>
            </div>
          </hlm-empty>
        }
      </div>
    </hlm-tabs>
  }

  <!-- ═══════════ STANDALONE ═══════════ -->
  @else {
    <hlm-tabs [tab]="standaloneTab" class="w-full">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <hlm-tabs-list variant="line" class="w-fit">
          <button hlmTabsTrigger="vouchers" (click)="onStandaloneTabChange('vouchers')">
            Gutscheine
          </button>
          <button hlmTabsTrigger="redemptions" (click)="onStandaloneTabChange('redemptions')">
            Einlösungen
          </button>
        </hlm-tabs-list>

        <app-date-range-filter
          [selected]="sharedDateRange"
          (changed)="onSharedDateRangeChange($event)" />
      </div>

      <div hlmTabsContent="vouchers" class="space-y-4 pt-4">
        <div #statisticsContainer>
          <app-statistics [statistics]="(statistics$ | async) || undefined" />
        </div>
        <ng-container *ngTemplateOutlet="searchBar"></ng-container>
        <app-voucher-history
          [vouchers]="vouchers"
          [loading]="vouchersLoading"
          [currentPage]="currentPage"
          [totalPages]="totalPages"
          [totalItems]="totalItems"
          [currentSortField]="currentSortField"
          [currentSortOrder]="currentSortOrder"
          (onPageChange)="onPageChange($event)"
          (onSort)="onSort($event)" />
      </div>

      <div hlmTabsContent="redemptions" class="space-y-4 pt-4">
        <section class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <app-kpi-card
            accent="default"
            label="Eingelöster Betrag"
            [value]="money(standaloneRedemptions?.total_redeemed_cents ?? 0)">
            <ng-template #icon><ng-icon name="lucideBanknote" /></ng-template>
          </app-kpi-card>
          <app-kpi-card
            accent="default"
            label="Anzahl Einlösungen"
            [value]="(standaloneRedemptions?.redemption_count ?? 0).toString()">
            <ng-template #icon><ng-icon name="lucideTicketCheck" /></ng-template>
          </app-kpi-card>
        </section>

        <ng-container *ngTemplateOutlet="searchBar"></ng-container>
        <app-redemption-table
          [records]="standaloneRedemptionRows"
          [loading]="standaloneRedemptionsLoading"
          [showStatusColumn]="false"
          [searchQuery]="activeSearch"
          emptyMessage="Keine Einlösungen im gewählten Zeitraum." />
      </div>
    </hlm-tabs>
  }
</div>

<!-- ── Shared search bar ── -->
<ng-template #searchBar>
  <div class="flex flex-wrap items-center justify-between gap-2 pt-4">
    <hlm-input-group class="w-full sm:w-64">
      <hlm-input-group-addon>
        <ng-icon name="lucideSearch" />
      </hlm-input-group-addon>
      <input
        hlmInputGroupInput
        type="search"
        [value]="voucherSearch"
        (input)="onSearchChange($any($event.target).value)"
        placeholder="Gutscheincode suchen…"
        aria-label="Gutscheincode suchen" />
      @if (voucherSearch) {
        <hlm-input-group-addon align="inline-end">
          <button hlmInputGroupButton aria-label="Suche zurücksetzen" (click)="clearSearch()">
            <ng-icon name="lucideX" />
          </button>
        </hlm-input-group-addon>
      }
    </hlm-input-group>

    @if (canCreateInternalVoucher && !company?.is_member) {
      <button hlmBtn variant="secondary" (click)="openInternalForm()">
        <span class="hidden sm:inline">Gutschein erstellen</span>
        <span class="sm:hidden">Erstellen</span>
      </button>
    }
  </div>
</ng-template>

<!-- ═══════════ Internal voucher dialog (raffles / giveaways) ═══════════ -->
<hlm-dialog
  [state]="showInternalForm ? 'open' : 'closed'"
  (stateChanged)="onInternalDialogState($event)">
  <hlm-dialog-content *hlmDialogPortal class="max-h-[90vh] max-w-2xl overflow-y-auto">
    <div hlmDialogHeader>
      <h3 hlmDialogTitle>Gutschein selbst erstellen</h3>
      <p hlmDialogDescription>
        Für Verlosungen und Geschenke. Es findet keine Zahlung statt, es fallen keine Gebühren an,
        und der Gutschein erscheint nicht in der Rechnungsübersicht oder in den Umsatzzahlen.
      </p>
    </div>

    @if (internalError) {
      <div hlmAlert variant="destructive">
        <ng-icon name="lucideTriangleAlert" />
        <p hlmAlertTitle>{{ internalError }}</p>
      </div>
    }

    <!-- ── Success state: created vouchers ── -->
    @if (internalCreated.length > 0) {
      <div hlmAlert>
        <ng-icon name="lucideCircleCheck" />
        <p hlmAlertTitle>{{ internalCreated.length }} Gutschein(e) erstellt</p>
        <p hlmAlertDescription>Lade die PDFs herunter, um sie zu verschenken.</p>
      </div>

      <div hlmItemGroup>
        @for (voucher of internalCreated; track voucher.code) {
          <div hlmItem variant="outline" size="sm">
            <div hlmItemContent>
              <span hlmItemTitle class="font-mono">{{ voucher.code }}</span>
              <span hlmItemDescription>{{ money(voucher.initial_amount) }}</span>
            </div>
            <div hlmItemActions>
              <button
                hlmBtn
                variant="outline"
                size="sm"
                [disabled]="isInternalDownloading(voucher.code) || internalBulkDownloading"
                (click)="downloadInternalPdf(voucher)">
                @if (isInternalDownloading(voucher.code)) {
                  <hlm-spinner />
                  Wird erstellt…
                } @else {
                  <ng-icon name="lucideDownload" />
                  PDF
                }
              </button>
            </div>
          </div>
        }
      </div>

      <div hlmDialogFooter>
        @if (internalCreated.length > 1) {
          <button
            hlmBtn
            variant="outline"
            [disabled]="internalBulkDownloading"
            (click)="downloadAllInternalPdfs()">
            @if (internalBulkDownloading) {
              <hlm-spinner />
              Lade {{ internalBulkProgress + 1 }} von {{ internalCreated.length }}…
            } @else {
              <ng-icon name="lucideDownload" />
              Alle PDFs herunterladen
            }
          </button>
        }
        <button hlmBtn [disabled]="internalBulkDownloading" (click)="closeInternalForm()">
          Fertig
        </button>
      </div>
    } @else {
      <!-- ── Form ── -->
      <form [formGroup]="internalForm" (ngSubmit)="submitInternalVoucher()" class="space-y-4">
        <div hlmField>
          <label hlmFieldLabel>Gutschein auswählen *</label>
          <div class="grid max-h-72 grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2">
            <!-- Value voucher: a div, not a button, because it holds the amount input -->
            <div
              hlmItem
              variant="outline"
              size="sm"
              class="data-selected:border-primary data-selected:bg-primary/5 cursor-pointer flex-col items-stretch"
              [attr.data-selected]="internalKind === 'money' ? 'true' : null"
              (click)="setInternalKind('money')">
              <div class="flex w-full items-start gap-2">
                <div hlmItemMedia variant="icon"><ng-icon name="lucideBanknote" /></div>
                <div hlmItemContent>
                  <span hlmItemTitle>Wertgutschein</span>
                  <span hlmItemDescription>Gutschein mit frei wählbarem Betrag</span>
                </div>
                @if (internalKind === 'money') {
                  <ng-icon name="lucideCircleCheck" class="text-primary" />
                }
              </div>

              @if (internalKind === 'money') {
                <div class="mt-2 w-full">
                  <input
                    hlmInput
                    id="internal-amount"
                    type="text"
                    formControlName="amount"
                    [placeholder]="'Betrag, z. B. ' + formatAmount(5000)"
                    [attr.aria-label]="'Betrag in ' + currencyName()"
                    class="w-full"
                    [attr.aria-invalid]="amountInvalid ? 'true' : null"
                    (click)="$event.stopPropagation()" />
                  @if (amountInvalid) {
                    <p class="text-destructive mt-1 text-xs">
                      Bitte einen Betrag zwischen {{ money(100) }} und {{ money(100000) }} angeben
                    </p>
                  }
                </div>
              }
            </div>

            <!-- Products -->
            @for (product of internalProducts; track product.public_id) {
              <button
                type="button"
                hlmItem
                variant="outline"
                size="sm"
                class="data-selected:border-primary data-selected:bg-primary/5 text-start"
                [attr.data-selected]="
                  internalKind === 'product' && selectedInternalProductId === product.public_id
                    ? 'true'
                    : null
                "
                (click)="selectInternalProduct(product)">
                <div hlmItemMedia variant="image">
                  @if (productImageUrl(product)) {
                    <img [src]="productImageUrl(product)" alt="" />
                  } @else {
                    <ng-icon name="lucideGift" />
                  }
                </div>
                <div hlmItemContent>
                  <span hlmItemTitle>{{ product.name }}</span>
                  @if (product.description) {
                    <span hlmItemDescription>{{ product.description }}</span>
                  }
                  <span hlmItemDescription>Wert: {{ money(product.price) }}</span>
                </div>
                @if (
                  internalKind === 'product' && selectedInternalProductId === product.public_id
                ) {
                  <ng-icon name="lucideCircleCheck" class="text-primary" />
                }
              </button>
            }
          </div>

          @if (internalProductsLoading) {
            <p hlmFieldDescription>Produkte werden geladen…</p>
          } @else if (internalProducts.length === 0) {
            <p hlmFieldDescription>
              Noch keine Produkte angelegt – unter „Produkte“ kannst du welche erstellen.
            </p>
          }
        </div>

        <div hlmField>
          <label hlmFieldLabel for="internal-quantity">Anzahl</label>
          <input
            hlmInput
            id="internal-quantity"
            type="number"
            min="1"
            max="20"
            formControlName="quantity"
            [attr.aria-invalid]="quantityInvalid ? 'true' : null" />
          <p hlmFieldDescription>Maximal 20 Gutscheine pro Vorgang.</p>
        </div>

        <div hlmField>
          <label hlmFieldLabel for="internal-note">Interne Notiz</label>
          <input
            hlmInput
            id="internal-note"
            type="text"
            formControlName="internal_note"
            placeholder="z. B. Verlosung Sommerfest 2026" />
          <p hlmFieldDescription>
            Nur für dich sichtbar – hilft später nachzuvollziehen, warum der Gutschein ausgestellt
            wurde.
          </p>
        </div>

        <div hlmField>
          <label hlmFieldLabel for="internal-recipient">Name der beschenkten Person</label>
          <input hlmInput id="internal-recipient" type="text" formControlName="recipient_name" />
        </div>

        <div hlmField>
          <label hlmFieldLabel for="internal-message">Persönliche Nachricht</label>
          <textarea
            hlmTextarea
            id="internal-message"
            rows="3"
            formControlName="personal_message"></textarea>
        </div>

        <div hlmDialogFooter>
          <button
            hlmBtn
            type="button"
            variant="outline"
            [disabled]="internalLoading"
            (click)="closeInternalForm()">
            Abbrechen
          </button>
          <button hlmBtn type="submit" [disabled]="internalLoading">
            @if (internalLoading) {
              <hlm-spinner />
              Wird erstellt…
            } @else {
              Erstellen
            }
          </button>
        </div>
      </form>
    }
  </hlm-dialog-content>
</hlm-dialog>
